import { createContext, useContext } from 'react';
import {
  LoaderFunctionArgs,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useQuery } from 'urql';
import { useJob } from '~/components/job/JobView';
import { AttributeTask } from '~/components/job/tasks/AttributeTask';
import { Custom } from '~/components/job/tasks/Custom';
import { MoveStock } from '~/components/job/tasks/MoveStock';
import { PurchaseTask } from '~/components/job/tasks/PurchaseTask';
import { Stocktake } from '~/components/job/tasks/Stocktake';
import { ErrorMessage } from '~/components/ui/Error';
import { JobStatus } from '~/generated/graphql';
import { TaskDocument } from '~/graphql/queries/Task';
import { isTaskViewDataReady } from '~/helpers/isTaskViewDataReady';
import { SideLayout } from '~/layouts/side/SideLayout';

type Job = NonNullable<ReturnType<typeof useJob>>['job'];

export const loader = ({ params }: LoaderFunctionArgs) => {
  const { taskId } = params;
  invariant(taskId, 'taskId is required');
  return { taskId };
};

type Context = {
  revalidate: () => void;
};
export const TaskViewContext = createContext({} as Context);

export const useTaskViewContext = () => useContext(TaskViewContext);

const TaskView = () => {
  const { taskId } = useParams();
  const { job, onSuccess } = useJob() as NonNullable<ReturnType<typeof useJob>>;

  const [result, reexecuteQuery] = useQuery({
    query: TaskDocument,
    variables: { id: taskId! },
    requestPolicy: 'cache-and-network',
  });

  const task = result.data?.task;
  const revalidate = () => reexecuteQuery({ requestPolicy: 'network-only' });
  const navigate = useNavigate();
  const location = useLocation();
  const { hash, pathname } = location;
  const messagesOpen = hash === '#messages-open' ? '#messages-open' : '';

  // Derive the absolute JobView path by stripping trailing /tasks or /tasks/<anything>
  const jobViewPath = pathname.replace(/\/tasks(?:\/.*)?$/, '');

  const canComplete = job.status === JobStatus.InProgress;

  const handleClose = () => {
    // Preserve upstream state (e.g. returnTo) so JobView knows how to close correctly.
    navigate(`${jobViewPath}${messagesOpen}`, {
      state: location.state,
      replace: true,
    });
  };

  // Only call onSuccess when task is actually completed/modified, not during navigation
  const handleSuccess = () => {
    onSuccess();
    handleClose();
  };

  // For navigation-only scenarios, don't trigger onSuccess
  const handleCloseOnly = () => {
    // Directly navigate preserving state
    navigate(`${jobViewPath}${messagesOpen}`, {
      state: location.state,
      replace: true,
    });
  };

  const handleReset = () => {
    if (!result.fetching) {
      revalidate();
    }
  };

  // Cache-first can keep the previous task while the route taskId has
  // already changed. Custom Formik state only initializes on mount, so
  // completing after a switch would save the previous task's notes/tags
  // onto the newly selected task.
  if (!isTaskViewDataReady(task, taskId)) {
    if (result.fetching || task) {
      return <Placeholder id={taskId} job={job} />;
    }
    return <ErrorMessage />;
  }

  if (task.type === 'Attribute') {
    return (
      <AttributeTask
        key={task.id}
        enabled={canComplete}
        job={job}
        task={task}
        onReset={handleReset}
        onSuccess={handleSuccess}
        onClose={handleCloseOnly}
      />
    );
  }

  if (task.purchase) {
    return (
      <PurchaseTask
        key={task.id}
        enabled={canComplete}
        job={job}
        task={task}
        onSuccess={handleSuccess}
        onClose={handleCloseOnly}
      />
    );
  }

  if (task.stocktake) {
    return (
      <Stocktake
        key={task.id}
        enabled={canComplete}
        job={job}
        task={task}
        onSuccess={handleSuccess}
        onClose={handleCloseOnly}
      />
    );
  }

  if (task.stockTransfer) {
    return (
      <MoveStock
        key={task.id}
        enabled={true}
        job={job}
        onSuccess={handleSuccess}
        onClose={handleCloseOnly}
      />
    );
  }

  return (
    <TaskViewContext.Provider value={{ revalidate }}>
      <Custom
        key={task.id}
        job={job}
        task={task}
        onClose={handleCloseOnly}
        onSuccess={handleSuccess}
        enabled={canComplete}
      />
    </TaskViewContext.Provider>
  );
};

const Placeholder = ({ id, job }: { id: string | undefined; job: Job }) => {
  const task = job.tasks.find((t) => t.id === id);
  return (
    <SideLayout>
      <SideLayout.Head onClose={() => {}}>{task?.name}</SideLayout.Head>
      <SideLayout.Body>&nbsp;</SideLayout.Body>
      <SideLayout.Foot className='p-4'>&nbsp;</SideLayout.Foot>
    </SideLayout>
  );
};

export default TaskView;
